import { Component, ElementRef, ViewChild } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import {
    IChangeCheckboxEventArgs,
    IChipsAreaReorderEventArgs,
    IComboSelectionChangingEventArgs,
    IgxDialogComponent,
    ISelectionEventArgs,
    OverlaySettings
} from '@infragistics/igniteui-angular';
import { DefaultValue } from 'src/app/models/default-value';
import { Level } from 'src/app/models/level';
import { Period, PeriodForm } from 'src/app/models/period';
import { Product } from 'src/app/models/product';
import { Program, ProgramForm } from 'src/app/models/program';
import { Sport } from 'src/app/models/sport';
import { TransportSchema } from 'src/app/models/transport-schema';
import { MessagesService } from 'src/app/services/messages.service';
import { ProgramsService } from 'src/app/services/programs.service';
import { WaitIndicatorService } from 'src/app/services/wait-indicator.service';
import { updateCollection } from 'src/app/shared/utils';
import { AddDiscountComponent } from '../add-discount/add-discount.component';
import { InfoProgramComponent } from '../info-program/info-program.component';

@Component({
    selector: 'app-edit-programs',
    templateUrl: './edit-programs.component.html',
    styleUrls: ['./edit-programs.component.scss']
})
export class EditProgramsComponent {
    @ViewChild('programDialog', { static: true, read: IgxDialogComponent })
    private programDialog?: IgxDialogComponent;

    @ViewChild('discount', { static: true, read: AddDiscountComponent })
    private discountComponent?: AddDiscountComponent;

    @ViewChild('infoProgram', { static: true, read: InfoProgramComponent })
    private infoProgram?: InfoProgramComponent;

    @ViewChild('outlet', { static: true, read: ElementRef })
    private outlet?: ElementRef;

    private defaultValue?: DefaultValue;
    private _editProgram!: Program;
    private periodToDelete: number = NaN;
    private _editProduct?: Product;
    private _dragProgram: Program | undefined;

    public programs: Program[] = [];
    public products: Product[] = [];
    public sports: Sport[] = [];
    public levels: Level[][] = [];
    public allLevels: Level[] = [];
    public defaultValues: DefaultValue[] = [];
    public transportSchemas: TransportSchema[] = [];
    public periods: Period[] = [];
    public customOverlaySettings = {};
    public programForm!: FormGroup<ProgramForm>;
    public dialogOkButtonText = 'Добави';
    public cannotDeletePeriod = true;
    public deleteDialogMessage = 'Проверява се възможността периода да се изтрие.';
    public sortPipeTrigger = 0;

    public get editProgram(): Program {
        return this._editProgram;
    }
    public set editProgram(v: Program) {
        this._editProgram = v;
    }

    constructor(
        private programService: ProgramsService,
        private msgs: MessagesService,
        private wait: WaitIndicatorService,
    ) {
        this.programForm = new FormGroup<ProgramForm>({
            name: new FormControl('', Validators.required),
            winter: new FormControl(false),
            depositDueDays: new FormControl(0),
            transportSchema: new FormControl(),
            periods: this.initPeriod()
        });
        this.load();
    }

    //#region Periods
    public periodsForm(): FormGroup<PeriodForm>[] {
        return this.programForm.controls.periods.controls;
    }

    public addPeriod() {
        const defaultValue = this.getDefaultValue();
        this.programForm?.controls.periods.push(this.initPeriod(defaultValue).at(0));
    }

    public async tryToDeletePeriod(e: Event, p: number, dialog: IgxDialogComponent) {
        e.stopPropagation();
        this.cannotDeletePeriod = true;
        this.deleteDialogMessage = 'Проверява се възможността периода да се изтрие.';
        this.periodToDelete = NaN;
        // TODO: remove os if this is fixed one day - https://github.com/IgniteUI/igniteui-angular/issues/15106
        const os: OverlaySettings = { outlet: this.outlet, closeOnOutsideClick: false, modal: true };
        dialog.open(os);
        this.wait.show();
        const periodId = this.periodsForm().at(p)?.controls.id.value;
        let canDelete = false;
        if (!periodId) {
            canDelete = true;
        } else {
            canDelete = await this.programService.periodHasPackages(periodId);
        }
        if (canDelete) {
            this.periodToDelete = p;
            this.cannotDeletePeriod = false;
            this.deleteDialogMessage = 'Периодът може да бъде изтрит.';
        } else {
            this.deleteDialogMessage = 'За избрания период има записани участници. Периодът не може да бъде изтрит!';
            this.periodToDelete = NaN;
        }
        this.wait.hide();
    }

    public async deletePeriod(dialog: IgxDialogComponent) {
        if (Number.isNaN(this.periodToDelete)) {
            dialog.close();
            this.deleteDialogMessage = 'Проверява се възможността периода да се изтрие.';
            return;
        }
        const periodId = this.periodsForm().at(this.periodToDelete)?.controls.id.value;
        if (!periodId) {
            this.programForm.controls.periods.removeAt(this.periodToDelete);
            dialog.close();
        } else {
            this.wait.show();
            const result = await this.programService.deletePeriod(periodId);
            if (result) {
                this.programForm.controls.periods.removeAt(this.periodToDelete);
                this.msgs.info('Периодът е успешно изтрит');
            } else {
                this.msgs.error('Възникна грешка. Периодът не е изтрит!');
            }
            this.wait.hide();
            dialog.close();
            this.deleteDialogMessage = 'Проверява се възможността периода да се изтрие.';
            this.periodToDelete = NaN;
        }
    }

    public generatePeriodDescription(period: FormGroup<PeriodForm>): string {
        const dates: Date[] = period.value.dates ?? [];
        if (!dates || dates.length < 1) {
            return 'Изберете дати за периода';
        }
        const start = dates[0].toLocaleDateString('bg-BG');
        const end = dates[dates.length - 1].toLocaleDateString('bg-BG');
        return `Период от ${start} до ${end}`;
    }

    public generatePeriodDescriptor(input: FormControl<Date[] | null>): string {
        let dates: Date[] = input.value ?? [];
        if (dates.length < 1) return '';
        if (dates.length < 2) return dates[0].toLocaleDateString('bg-BG');

        dates = dates.sort((a, b) => a.getTime() - b.getTime());
        const ranges: Date[][] = [];
        let rangeIndex = 0;
        ranges[rangeIndex] = [dates[0]];
        for (let i = 1; i < dates.length; i++) {
            const prev = new Date(dates[i - 1]);
            const next = new Date(dates[i]);
            if (prev.getMonth() === next.getMonth() &&
                prev.getFullYear() === next.getFullYear() &&
                new Date(prev.setDate(prev.getDate() + 1)).getDate() === next.getDate()) {
                ranges[rangeIndex].push(dates[i])
            } else {
                rangeIndex++;
                ranges.push([dates[i]]);
            }
        }

        let result = '';
        if (ranges.length === 1) {
            result = this.parseDateRange(ranges[0], true);
        } else {
            for (let range of ranges) {
                result += this.parseDateRange(range);
                const month = range[0].getMonth() + 1;
                const year = range[0].getFullYear();
                const index = ranges.indexOf(range);
                const nextMonth = ranges[index + 1] ? ranges[index + 1][0].getMonth() + 1 : -1;
                const nextYear = ranges[index + 1] ? ranges[index + 1][0].getFullYear() : -1;
                if (month !== nextMonth) {
                    result += ' ';
                    result += range[0].toLocaleDateString('bg-BG', { month: 'long' })
                }
                if (nextYear !== year) {
                    result += ' ';
                    result += range[0].toLocaleDateString('bg-BG', { year: 'numeric' })
                }
                const position = ranges.length - 2 - ranges.indexOf(range);
                if (position > 0) {
                    result += ', ';
                } else if (position === 0) {
                    result += ' и ';
                }
            }
        }
        return result;
    }

    public hasPeriods(): boolean {
        return !!this.periodsForm().every(p => p.controls.dates?.value?.length);
    }
    //#endregion Periods

    //#region Products    
    public sortProducts(): void {
        this.products = [];
        if (!this.editProgram)
            return;

        this.products = [...this.editProgram.products?.sort((x, y) => x.order - y.order)];
        for (let i = 0; i < this.products.length; i++) {
            this.products[i].order = i;
        }
    }

    public addProduct(input: HTMLInputElement) {
        if (input.value === "" || input.value == undefined)
            return;

        if (!this._editProduct) {
            this._editProduct = {
                id: this.editProgram.products.length.toString(),
                name: '',
                order: Math.max(...this.editProgram.products.map(p => p.order)) + 1,
                program: this.editProgram,
            };
            this.editProgram.products.push(this._editProduct);
            this.sortProducts();
        }

        this._editProduct.name = input.value;
        input.value = "";
        this._editProduct = undefined;
    }

    public editProduct(e: Event, product: Product, input: HTMLInputElement) {
        e.stopPropagation();
        this._editProduct = product;
        input.value = product.name;
    }

    public productsOrderChanged(e: IChipsAreaReorderEventArgs) {
        const products: Product[] = [];
        for (const order of e.chipsArray) {
            const product = this.editProgram.products.find(p => p.order === +order.id);
            products.push(product!);
        }
        for (let order = 0; order < products.length; order++) {
            const product = products[order];
            product.order = order;
        }
        this.editProgram.products = products;
        this.sortProducts();
    }
    //#endregion Products

    //#region Program order    
    public programDragStart(program: Program) {
        this._dragProgram = program;
    }

    public programGhostCreate(dragRef: HTMLElement) {
        dragRef.style.visibility = 'hidden';
    }

    public programDragEnter(p: Program) {
        if (!this._dragProgram || p.id === this._dragProgram.id)
            return;

        const oldOrder = this._dragProgram!.order;
        this._dragProgram!.order = p.order;
        p.order = oldOrder;
        this.sortPipeTrigger++;
    }

    async programDragEnd(dragRef: HTMLElement) {
        dragRef.style.visibility = 'visible';
        const data = this.programs.map(p => ({ id: p.id!, order: p.order }));
        this.programs = await this.programService.saveProgramsOrder(data);
    }
    //#endregion Program order

    public async setProgramHidden(program: Program, hidden: boolean) {
        try {
            program.deleted = hidden;
            program = await this.programService.delete({ id: program.id!, deleted: hidden });

            this.programs = [...this.programs];
            if (hidden) {
                this.msgs.success("Програмата беше изтрита успешно");
            } else {
                this.msgs.success("Програмата беше възстановена успешно");
            }
        }
        catch (e) {
            this.msgs.error(e);
        }
    }

    public async addDiscounts(program: Program, dialog: IgxDialogComponent): Promise<void> {
        this.discountComponent!.program = await this.programService.byId(program.id!);
        dialog.open();
    }

    public programInfo(program: Program, dialog: IgxDialogComponent): void {
        this.infoProgram!.program = program;
        dialog.open();
    }

    public startCreateProgram(e: IgxDialogComponent) {
        this.dialogOkButtonText = 'Добави';
        this.programForm = new FormGroup<ProgramForm>({
            name: new FormControl('', Validators.required),
            winter: new FormControl(false),
            depositDueDays: new FormControl(this.defaultValue?.depositDueDays ?? 0),
            transportSchema: new FormControl(),
            periods: this.initPeriod(this.defaultValue)
        });
        this.editProgram = this.getEmptyProgram();
        // TODO: remove os if this is fixed one day - https://github.com/IgniteUI/igniteui-angular/issues/15106
        const os: OverlaySettings = { outlet: this.outlet, closeOnOutsideClick: false, closeOnEscape: true, modal: true };
        e.open(os);
    }

    public closeProgramDialog() {
        this.programForm.reset();
        this.editProgram = this.getEmptyProgram();
        this.programDialog?.close();
    }

    public programUpdate() {
        this.programSubmit(true);
        this.dialogOkButtonText = 'Редактирай';
    }

    public programUpdateAndClose() {
        this.programSubmit();
        this.closeProgramDialog();
    }

    public programSubmit(startUpdate = false) {
        this.editProgram.name = this.programForm.value.name ?? '';
        this.editProgram.winter = this.programForm.value.winter ?? false;
        this.editProgram.depositDueDays = this.programForm.value.depositDueDays ?? 0;

        if (this.programForm.value.transportSchema) {
            this.editProgram.transportSchema = this.transportSchemas.find(ts => ts.id === this.programForm.value.transportSchema);
        }
        this.editProgram.periods = [];
        for (const periodVal of this.programForm.getRawValue().periods ?? []) {
            let period = periodVal;
            if (period.transportSchema === this.programForm.value.transportSchema) {
                period.transportSchema = null;
            }

            this.editProgram.periods.push(period as any);
        }
        for (const product of this.editProgram.products) {
            (product as any).program = product.program?.id;
        }

        if (this.dialogOkButtonText === 'Добави') {
            this.createProgram(this.editProgram, startUpdate);
        } else if (this.dialogOkButtonText === 'Редактирай') {
            this.updateProgram(this.editProgram);
        }
    }

    public async startProgramUpdate(program: Program) {
        this.wait.show();
        this.editProgram = await this.programService.byId(program.id!);
        this.sortProducts();
        const programFrom = this.programToProgramForm(this.editProgram);
        this.programForm = new FormGroup<ProgramForm>(programFrom);
        this.dialogOkButtonText = 'Редактирай';
        this.wait.hide();
        // TODO: remove os if this is fixed one day - https://github.com/IgniteUI/igniteui-angular/issues/15106
        const os: OverlaySettings = { outlet: this.outlet, closeOnOutsideClick: false, modal: true };
        this.programDialog?.open(os);
    }

    public transportSchemaChanged(e: ISelectionEventArgs) {
        for (const period of this.programForm.controls.periods.controls) {
            period.controls.transportSchema.reset(e.newSelection.value);
        }
    }

    public formatLabel(value: number) {
        return Math.round(value);
    }

    public openCalendarDialog(e: Event, dialog: IgxDialogComponent) {
        e.stopPropagation();
        // TODO: remove os if this is fixed one day - https://github.com/IgniteUI/igniteui-angular/issues/15106
        const os: OverlaySettings = { outlet: this.outlet, closeOnOutsideClick: false, modal: true };
        dialog.open(os);
    }

    public datesSelected(e: Date | Date[], period: FormGroup<PeriodForm>) {
        if (period.controls.package.value === false)
            return;

        const dates = e as Date[];
        if (dates === null)
            return;

        period.controls.packageDuration.setValue(dates.length);
        period.controls.description.setValue(this.generatePeriodDescriptor(period.controls.dates));
    }

    public sportSelected(e: IComboSelectionChangingEventArgs, p: number) {
        this.levels[p] = [];
        const levelForm = this.programForm.controls.periods.at(p).controls.levels;
        levelForm.reset([]);
        if (e.newSelection.length === 1) {
            levelForm.enable();
            const sport = this.sports.find(s => s.id === e.newSelection[0].id);
            this.loadLevelsForSport(sport!, p);
        } else {
            levelForm.disable();
        }
    }

    public isPackageChange(e: IChangeCheckboxEventArgs, p: number) {
        const period = this.programForm.controls.periods.controls.at(p);
        if (!period)
            return;
        if (e.checked) {
            period.controls.packageDuration.setValue(period.controls.dates.value?.length ?? 0);
            period.controls.packageDuration.disable();
            period.controls.description.enable();
            period.controls.description.setValue(this.generatePeriodDescriptor(period.controls.dates));
        } else {
            period.controls.packageDuration.enable();
            period.controls.description.setValue(null);
            period.controls.description.disable();
        }
    }

    private getDefaultValue(): DefaultValue | undefined {
        let defaultValue = this.defaultValues.find(d => d.parentId === this.editProgram.id);
        if (!defaultValue) {
            defaultValue = this.defaultValue;
        }
        return defaultValue;
    }

    private initPeriod(defaultValue?: DefaultValue): FormArray<FormGroup<PeriodForm>> {
        const periods = new FormArray([new FormGroup<PeriodForm>({
            id: new FormControl(null),
            disabled: new FormControl(false),
            sports: new FormControl([]),
            levels: new FormControl([]),
            dates: new FormControl([], Validators.required),
            ageRange: new FormControl({ lower: defaultValue?.ageRange?.lower ?? 6, upper: defaultValue?.ageRange?.upper ?? 14 }, Validators.required),
            packageDuration: new FormControl(defaultValue?.packageDuration ?? 0, Validators.required),
            capacity: new FormControl(defaultValue?.capacity ?? 0, Validators.required),
            price: new FormControl(defaultValue?.price ?? 0, Validators.required),
            deposit: new FormControl(defaultValue?.deposit ?? 0, Validators.required),
            description: new FormControl(null),
            product: new FormControl(null),
            transportSchema: new FormControl(this.editProgram?.transportSchema?.id ?? null),
            package: new FormControl(false),
        })]);

        return periods;
    }

    private getEmptyProgram(): Program {
        const result: Program = {
            name: '',
            winter: false,
            depositDueDays: 0,
            order: -1,
            discounts: [],
            products: [],
            periods: [],
            packages: [],
        }
        return result;
    }

    private programToProgramForm = (program: Program): ProgramForm => {
        const programForm: ProgramForm = {
            name: new FormControl(program.name),
            winter: new FormControl(program.winter),
            depositDueDays: new FormControl(program.depositDueDays ?? null),
            transportSchema: new FormControl(program.transportSchema?.toString() ?? null),
            periods: new FormArray<FormGroup<PeriodForm>>([])
        };
        program.periods.sort((x, y) => (x.dates[0]?.getTime() ?? 0) - (y.dates[0]?.getTime() ?? 0)).forEach((p, i) => {
            const periodForm: PeriodForm = {
                id: new FormControl(p.id ?? null),
                disabled: new FormControl(p.disabled ?? null),
                sports: new FormControl(p.sports.map(s => s.id!)),
                levels: new FormControl(p.levels.map(l => l.id!)),
                dates: new FormControl(p.dates),
                ageRange: new FormControl(p.ageRange),
                packageDuration: new FormControl(p.packageDuration),
                capacity: new FormControl(p.capacity),
                price: new FormControl(p.price),
                deposit: new FormControl(p.deposit),
                description: new FormControl(p.description),
                package: new FormControl(p.package),
                product: new FormControl(p.product?.id ?? null),
                transportSchema: new FormControl(p.transportSchema?.toString() ?? program.transportSchema?.toString() ?? null),
            };
            if (p.package) {
                periodForm.packageDuration.disable()
                periodForm.packageDuration.setValue(periodForm.dates.value?.length ?? 0);
            }
            if (p.sports.length === 1) {
                this.loadLevelsForSport(p.sports[0], i);
            }
            programForm.periods.push(new FormGroup<PeriodForm>(periodForm));
        });

        return programForm;
    }

    private loadLevelsForSport(sport: Sport, index: number) {
        this.levels[index] = this.allLevels.filter(l => l.sport.toString() === sport?.id);
    }

    private createProgram = async (program: Program, startUpdate = false) => {
        try {
            this.wait.show();
            program.order = -1;
            this.editProgram = await this.programService.addProgram(program);
            this.sortProducts();
            const programFrom = this.programToProgramForm(this.editProgram);
            this.programForm = new FormGroup<ProgramForm>(programFrom);

            this.msgs.success(`Програма "${program.name}" е успешно добавена.`);
            this.dialogOkButtonText = 'Редактирай';
            if (startUpdate) {
                this.startProgramUpdate(this.editProgram);
            }
            this.programs = [...this.programs, this.editProgram];
            this.wait.hide();
        }
        catch (err) {
            this.msgs.error(err);
            this.wait.hide();
        }
    }

    private updateProgram = async (program: Program) => {
        try {
            this.wait.show();
            this.editProgram = await this.programService.updateProgram(program);
            this.sortProducts();
            const programFrom = this.programToProgramForm(this.editProgram);
            this.programForm = new FormGroup<ProgramForm>(programFrom);

            this.msgs.success(`Пограма "${program.name}" е успешно редактирана.`);
            this.programs = updateCollection(this.editProgram, this.programs);
            this.wait.hide();
        }
        catch (err) {
            this.msgs.error(err);
            this.wait.hide();
        }
    }

    private parseDateRange = (dates: Date[], singleRange: boolean = false): string => {
        const start = dates[0];
        const startDate = start.getDate();
        const end = dates[dates.length - 1];
        const endDate = end!.getDate();
        if (singleRange) {
            return `${startDate}-${endDate} ${end!.toLocaleDateString('bg-BG', { month: 'long', year: 'numeric' })}`;
        } else {
            const datesJoin = dates.map(d => d.getDate()).join(', ');
            return datesJoin;
        }
    }

    private async load() {
        this.wait.show();
        const data = await this.programService.initialData();
        this.programs = data.programs;
        this.sports = data.sports;
        this.allLevels = data.levels;
        this.defaultValues = data.defaults;
        this.defaultValue = this.defaultValues.find(v => !v.parentId);
        this.transportSchemas = data.schemas;
        this.wait.hide();
    }
}
